import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Plus, Send, ArrowRightLeft, Trash2, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuotesList() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    notes: "", valid_until: "",
  });
  const [items, setItems] = useState([{ name: "", quantity: 1, unit_price: 0 }]);

  const fetchQuotes = async () => {
    try {
      const { data } = await axios.get(`${API}/oms/quotes`, { withCredentials: true });
      setQuotes(data);
    } catch (err) {
      console.error("Failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotes(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const total = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
      await axios.post(`${API}/oms/quotes`, {
        ...form,
        items,
        total_amount: total,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      }, { withCredentials: true });
      setShowCreate(false);
      setForm({ customer_name: "", customer_email: "", customer_phone: "", notes: "", valid_until: "" });
      setItems([{ name: "", quantity: 1, unit_price: 0 }]);
      fetchQuotes();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed");
    }
  };

  const sendQuote = async (id) => {
    try {
      await axios.patch(`${API}/oms/quotes/${id}/send`, {}, { withCredentials: true });
      fetchQuotes();
    } catch (err) {
      alert("Failed to send");
    }
  };

  const convertToOrder = async (id) => {
    try {
      await axios.patch(`${API}/oms/quotes/${id}/convert`, {}, { withCredentials: true });
      fetchQuotes();
      alert("Quote converted to order!");
    } catch (err) {
      alert("Failed to convert");
    }
  };

  const STATUS_MAP = {
    draft: "bg-zinc-700 text-zinc-300",
    sent: "bg-blue-500/20 text-blue-400",
    converted: "bg-green-500/20 text-green-400",
  };

  return (
    <div data-testid="quotes-list" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400" /> Quotes
        </h2>
        <Button onClick={() => setShowCreate(!showCreate)} className="bg-gradient-to-r from-orange-500 to-red-600 text-white w-full sm:w-auto" data-testid="create-quote-btn">
          <Plus className="w-4 h-4 mr-2" /> New Quote
        </Button>
      </div>

      {showCreate && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-4">
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Customer Name *</Label>
                  <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" required />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Email *</Label>
                  <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" required />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Valid Until</Label>
                  <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
                </div>
              </div>

              {items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="text-zinc-400 text-xs">Item</Label>
                    <Input value={item.name} onChange={(e) => { const u = [...items]; u[idx].name = e.target.value; setItems(u); }} className="bg-zinc-800 border-zinc-700 text-white" placeholder="Item name" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Qty</Label>
                    <Input type="number" min={1} value={item.quantity} onChange={(e) => { const u = [...items]; u[idx].quantity = parseInt(e.target.value) || 1; setItems(u); }} className="bg-zinc-800 border-zinc-700 text-white" />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-zinc-400 text-xs">Price ($)</Label>
                      <Input type="number" step="0.01" min={0} value={item.unit_price} onChange={(e) => { const u = [...items]; u[idx].unit_price = parseFloat(e.target.value) || 0; setItems(u); }} className="bg-zinc-800 border-zinc-700 text-white" />
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => items.length > 1 && setItems(items.filter((_, i) => i !== idx))} className="text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}

              <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { name: "", quantity: 1, unit_price: 0 }])} className="border-zinc-700 text-zinc-400 border-dashed">
                <Plus className="w-3 h-3 mr-1" /> Add Item
              </Button>

              <div className="flex items-center justify-between">
                <span className="text-white font-bold">Total: ${items.reduce((s, i) => s + i.quantity * i.unit_price, 0).toFixed(2)}</span>
                <Button type="submit" className="bg-orange-500 text-white">Create Quote</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-zinc-500 text-center py-12">Loading...</div>
      ) : quotes.length === 0 ? (
        <div className="text-zinc-500 text-center py-12">No quotes yet</div>
      ) : (
        <div className="space-y-2">
          {quotes.map((q) => (
            <div key={q.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-medium">{q.customer_name}</span>
                  <Badge className={STATUS_MAP[q.status]}>{q.status}</Badge>
                </div>
                <p className="text-xs text-zinc-500">{q.items?.map((i) => `${i.name} x${i.quantity}`).join(", ")}</p>
                <p className="text-xs text-zinc-600">{new Date(q.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-white font-bold">${q.total_amount?.toFixed(2)}</span>
                {q.status === "draft" && (
                  <Button size="sm" variant="outline" className="border-blue-500/30 text-blue-400" onClick={() => sendQuote(q.id)} data-testid={`send-quote-${q.id}`}>
                    <Send className="w-3 h-3 mr-1" /> Send
                  </Button>
                )}
                {q.status !== "converted" && (
                  <Button size="sm" variant="outline" className="border-green-500/30 text-green-400" onClick={() => convertToOrder(q.id)} data-testid={`convert-quote-${q.id}`}>
                    <ArrowRightLeft className="w-3 h-3 mr-1" /> Convert
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
